import robotoVfs from 'pdfmake/build/vfs_fonts';
import arabicRegular from '@/assets/fonts/pdf/NotoSansArabic-Regular.ttf?url';
import arabicBold from '@/assets/fonts/pdf/NotoSansArabic-Bold.ttf?url';
import devanagariRegular from '@/assets/fonts/pdf/NotoSansDevanagari-Regular.ttf?url';
import devanagariBold from '@/assets/fonts/pdf/NotoSansDevanagari-Bold.ttf?url';
import japaneseRegular from '@/assets/fonts/pdf/NotoSansJP-Regular.otf?url';
import japaneseBold from '@/assets/fonts/pdf/NotoSansJP-Bold.otf?url';
import chineseRegular from '@/assets/fonts/pdf/NotoSansSC-Regular.otf?url';
import chineseBold from '@/assets/fonts/pdf/NotoSansSC-Bold.otf?url';

export type PdfFont = 'NotoSansArabic' | 'NotoSansDevanagari' | 'NotoSansJP' | 'NotoSansSC';

export interface FontDefinition {
  normal: string;
  bold: string;
  italics: string;
  bolditalics: string;
}

export interface LoadedFonts {
  fonts?: Record<string, FontDefinition>;
  vfs?: Record<string, string>;
}

const FONT_FILES: Record<PdfFont, { normal: string; bold: string }> = {
  NotoSansArabic: { normal: arabicRegular, bold: arabicBold },
  NotoSansDevanagari: { normal: devanagariRegular, bold: devanagariBold },
  NotoSansJP: { normal: japaneseRegular, bold: japaneseBold },
  NotoSansSC: { normal: chineseRegular, bold: chineseBold },
};

// Passing fonts to createPdf replaces pdfmake's built-in list, so Roboto has to be listed again.
const ROBOTO: FontDefinition = {
  normal: 'Roboto-Regular.ttf',
  bold: 'Roboto-Medium.ttf',
  italics: 'Roboto-Italic.ttf',
  bolditalics: 'Roboto-MediumItalic.ttf',
};

const ARABIC = /\p{scx=Arabic}/u;
const DEVANAGARI = /\p{scx=Devanagari}/u;
// CJK punctuation and full-width forms are Common script, and Roboto has no glyphs for them.
const CJK = /[\p{scx=Han}\p{scx=Hiragana}\p{scx=Katakana}\u3000-\u303f\uff00-\uffef]/u;
// Script, not scx: Chinese text uses 、 and 。, whose scx includes Hiragana.
const KANA = /[\p{Script=Hiragana}\p{Script=Katakana}]/u;
const NON_LATIN = /[\p{scx=Arabic}\p{scx=Devanagari}\p{scx=Han}\p{scx=Hiragana}\p{scx=Katakana}\u3000-\u303f\uff00-\uffef]/u;
const WHITESPACE = /\s/u;

const CONTAINER_KEYS = new Set(['text', 'stack', 'ul', 'ol', 'columns', 'table', 'body']);

const fontCache = new Map<string, Promise<string>>();

export const cjkFontFor = (text: string): PdfFont => {
  return KANA.test(text) ? 'NotoSansJP' : 'NotoSansSC';
};

const fontForChar = (char: string, cjkFont: PdfFont): PdfFont | undefined => {
  if (ARABIC.test(char)) {
    return 'NotoSansArabic';
  }
  if (DEVANAGARI.test(char)) {
    return 'NotoSansDevanagari';
  }
  if (CJK.test(char)) {
    return cjkFont;
  }
  return undefined;
};

export const requiredFonts = (text: string): PdfFont[] => {
  const cjkFont = cjkFontFor(text);
  const fonts = new Set<PdfFont>();
  for (const char of text) {
    const font = fontForChar(char, cjkFont);
    if (font) {
      fonts.add(font);
    }
  }
  return [...fonts];
};

interface TextRun {
  text: string;
  font?: PdfFont;
}

// pdfmake has no per-glyph fallback, so each run of one script gets its own font. Latin stays in the default Roboto.
const splitRuns = (text: string, cjkFont: PdfFont): TextRun[] => {
  const runs: TextRun[] = [];
  for (const char of text) {
    const current = runs[runs.length - 1];
    const font = WHITESPACE.test(char) && current ? current.font : fontForChar(char, cjkFont);
    if (current && current.font === font) {
      current.text += char;
    } else {
      runs.push(font ? { text: char, font } : { text: char });
    }
  }
  return runs;
};

export const assignFonts = (node: unknown, cjkFont: PdfFont): unknown => {
  if (typeof node === 'string') {
    return NON_LATIN.test(node) ? { text: splitRuns(node, cjkFont) } : node;
  }
  if (Array.isArray(node)) {
    return node.map(child => assignFonts(child, cjkFont));
  }
  if (!node || typeof node !== 'object') {
    return node;
  }
  return Object.fromEntries(
    Object.entries(node).map(([key, value]) => {
      if (!CONTAINER_KEYS.has(key)) {
        return [key, value];
      }
      if (key === 'text' && typeof value === 'string') {
        return [key, NON_LATIN.test(value) ? splitRuns(value, cjkFont) : value];
      }
      return [key, assignFonts(value, cjkFont)];
    })
  );
};

const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve((reader.result as string).split(',')[1]);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
};

const fetchFont = (url: string): Promise<string> => {
  const cached = fontCache.get(url);
  if (cached) {
    return cached;
  }
  const request = fetch(url)
    .then(resp => {
      if (!resp.ok) {
        throw new Error(`HTTP ${resp.status}`);
      }
      return resp.blob();
    })
    .then(blobToBase64);
  request.catch(() => fontCache.delete(url));
  fontCache.set(url, request);
  return request;
};

export const loadFonts = async (names: PdfFont[]): Promise<LoadedFonts> => {
  if (names.length === 0) {
    return {};
  }
  const fonts: Record<string, FontDefinition> = { Roboto: { ...ROBOTO } };
  const vfs: Record<string, string> = { ...robotoVfs };
  await Promise.all(
    names.map(async name => {
      const regular = `${name}-Regular`;
      const bold = `${name}-Bold`;
      const [regularData, boldData] = await Promise.all([fetchFont(FONT_FILES[name].normal), fetchFont(FONT_FILES[name].bold)]);
      vfs[regular] = regularData;
      vfs[bold] = boldData;
      fonts[name] = { normal: regular, bold, italics: regular, bolditalics: bold };
    })
  );
  return { fonts, vfs };
};
